// @flow

import PropTypes from 'prop-types';
import React from 'react';

/**
 * Abstract (base) class for an item in {@link Toolbox}. The item can be located
 * anywhere in the {@link Toolbox}, it will morph its shape to accommodate it.
 *
 * @abstract
 */
export default class AbstractToolboxItem extends React.PureComponent {
  /**
   * Default values for {@code AbstractToolboxItem} component's properties.
   *
   * @static
   */
  static defaultProps = {
    disabled: false,
    label: '',
    showLabel: false,
    t: undefined,
    tooltip: '',
    tooltipPosition: 'right',
    visible: true
  };

  /**
   * Initializes a new {@code AbstractToolboxItem} instance.
   *
   * @param {Object} props - The React {@code Component} props to initialize
   * the new {@code AbstractToolboxItem} instance with.
   */
  constructor(props) {
    super(props);

    // Bind event handlers so they are only bound once per instance.
    this._onClick = this._onClick.bind(this);
  }

  /**
   * Helper property to get the item label. If a translation function was
   * provided then it will be translated using it.
   *
   * @protected
   * @returns {?string}
   */
  get label() {
    return this._maybeTranslateAttribute(
      this.props.label,
      this.props.labelProps
    );
  }

  /**
   * Helper property to get the item tooltip. If a translation function was
   * provided then it will be translated using it.
   *
   * @protected
   * @returns {?string}
   */
  get tooltip() {
    return this._maybeTranslateAttribute(this.props.tooltip);
  }

  /**
   * Helper property to get the item accessibilityLabel. If a translation
   * function was provided then it will be translated using it.
   *
   * @protected
   * @returns {?string}
   */
  get accessibilityLabel() {
    return this._maybeTranslateAttribute(this.props.accessibilityLabel);
  }

  /**
   * Utility function to translate the given string, if a translation
   * function is available.
   *
   * @param {string} text - What needs translating.
   * @param {string} textProps - Additional properties for translation text.
   * @private
   * @returns {string}
   */
  _maybeTranslateAttribute(text, textProps) {
    const { t } = this.props;

    if (textProps) {
      return typeof t === 'function'
        ? t(text, textProps)
        : `${text} ${textProps}`;
    }

    return typeof t === 'function' ? t(text) : text;
  }

  /**
   * Handles clicking/pressing this {@code AbstractToolboxItem} by
   * forwarding the event to the {@code onClick} prop of this instance if any.
   *
   * @protected
   * @returns {void}
   */
  _onClick(...args) {
    const { disabled, onClick } = this.props;
    disabled || (onClick && onClick(...args));
  }

  /**
   * Renders this {@code AbstractToolboxItem} (if it is {@code visible}). To
   * be implemented/overridden by extenders. The default implementation of
   * {@code AbstractToolboxItem} does nothing.
   *
   * @protected
   * @returns {ReactElement}
   */
  _renderItem() {
    // To be implemented by a subclass.
    return null;
  }

  /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
  render() {
    return this.props.visible ? this._renderItem() : null;
  }
}

AbstractToolboxItem.propTypes = {
  /**
   * A succinct description of what the item does. Used by accessibility
   * tools and torture tests.
   */
  accessibilityLabel: PropTypes.string,

  /**
   * An extra class name to be added at the end of the element's class name
   * in order to enable custom styling.
   */
  customClass: PropTypes.string,

  /**
   * Whether this item is disabled or not. When disabled, clicking an the item
   * has no effect, and it may reflect on its style.
   */
  disabled: PropTypes.bool,

  /**
   * A React Element to display at the end of {@code ToolboxItem}.
   */
  elementAfter: PropTypes.any,

  /**
   * The icon to render for this {@code ToolboxItem}.
   */
  icon: PropTypes.any,

  /**
   * The text associated with this item. When `showLabel` is set to
   * {@code true}, it will be displayed alongside the icon.
   */
  label: PropTypes.string,
  labelProps: PropTypes.object,
  /**
   * On click handler.
   */
  onClick: PropTypes.func,

  /**
   * Whether to show the label or not.
   */
  showLabel: PropTypes.bool,

  /**
   * Collection of styles for the item. Used only on native.
   */
  styles: PropTypes.object,

  /**
   * Invoked to obtain translated strings.
   */
  t: PropTypes.func,

  /**
   * True if the item is toggled, false otherwise.
   */
  toggled: PropTypes.bool,

  /**
   * The text to display in the tooltip. Used only on web.
   */
  tooltip: PropTypes.string,

  /**
   * From which direction the tooltip should appear, relative to the
   * item. Used only on web.
   */
  tooltipPosition: PropTypes.string,

  /**
   * Whether this item is visible or not.
   */
  visible: PropTypes.bool,
  transparent: PropTypes.bool
};
